import { type AGUIEvent, EventType } from '@ag-ui/core';
import type { ChatMessageChunk } from '@mui/x-chat/types';
import { AGENTIC_PLATFORM_ASK_USER_TOOL_NAME } from 'agentic-platform-schema';
import { z } from 'zod';

import { isDefined, isTruthy } from 'common';

import { parseJsonOrFallbackToValue } from '../../../utils/json';
import { getAgenticChatQuestionPart, parseAgenticChatQuestion } from '../Question/utils';

/**
 * The `CUSTOM` event the agentic platform emits while a tool is still producing output.
 *
 * @see backend/agentic-platform/schema/src/agUi/event.ts
 */
const TOOL_CALL_PARTIAL_RESULT_EVENT_NAME = 'toolCallPartialResult';

const ToolCallPartialResultSchema = z.object({
  partialResult: z.unknown(),
  toolCallId: z.string(),
});

export interface AgenticChatRunTranslator {
  /** Translates one protocol event into the chunks the chat runtime consumes. */
  getChunks: (event: AGUIEvent) => ChatMessageChunk[];
  /** The failure reported by `RUN_ERROR`, which the protocol delivers in-band on a 200 stream. */
  getRunErrorMessage: () => string | undefined;
}

/**
 * Projects one AG-UI run onto a single assistant message.
 *
 * A run can span several protocol messages — text, then reasoning, then tool calls — but the
 * chat runtime models that as one assistant message made of many parts, so protocol message ids
 * become part ids and the assistant message id is fixed for the run.
 *
 * The translator is stateful and must not be shared between runs.
 */
export function createAgenticChatRunTranslator(options: {
  assistantMessageId: string;
}): AgenticChatRunTranslator {
  const toolCallArgumentsById = new Map<string, string>();
  const toolCallNamesById = new Map<string, string>();
  const deferredStartChunksById = new Map<string, ChatMessageChunk>();
  const startedPartIds = new Set<string>();
  let hasRunProducedContent = false;
  let runErrorMessage: string | undefined;
  let runStartedAtMilliseconds: number | undefined;
  let runStartedAtEventMilliseconds: number | undefined;

  /**
   * Holds a part's start back until something lands in it.
   *
   * A part whose content never arrives is never started, so an empty text bubble or an expander
   * with nothing behind it cannot reach the transcript.
   */
  function deferPartStart(options: {
    id: string;
    startChunk: ChatMessageChunk;
  }): ChatMessageChunk[] {
    const { id, startChunk } = options;
    deferredStartChunksById.set(id, startChunk);
    return [];
  }

  function getPartDeltaChunks(options: {
    delta: string;
    deltaChunk: ChatMessageChunk;
    id: string;
  }): ChatMessageChunk[] {
    const { delta, deltaChunk, id } = options;
    if (!isTruthy(delta.length)) {
      return [];
    }
    hasRunProducedContent = true;
    const deferredStartChunk = deferredStartChunksById.get(id);
    if (!isDefined(deferredStartChunk)) {
      return [deltaChunk];
    }
    deferredStartChunksById.delete(id);
    startedPartIds.add(id);
    return [deferredStartChunk, deltaChunk];
  }

  function isAskUserCall(toolCallId: string): boolean {
    return toolCallNamesById.get(toolCallId) === AGENTIC_PLATFORM_ASK_USER_TOOL_NAME;
  }

  function getPartEndChunks(options: {
    id: string;
    endChunk: ChatMessageChunk;
  }): ChatMessageChunk[] {
    const { id, endChunk } = options;
    deferredStartChunksById.delete(id);
    return startedPartIds.has(id) ? [endChunk] : [];
  }

  function getChunks(event: AGUIEvent): ChatMessageChunk[] {
    switch (event.type) {
      case EventType.RUN_STARTED: {
        const runReceivedAtMilliseconds = Date.now();
        runStartedAtMilliseconds = runReceivedAtMilliseconds;
        runStartedAtEventMilliseconds = event.timestamp;
        return [
          { messageId: options.assistantMessageId, type: 'start' },
          {
            metadata: { runStartedAtMilliseconds: runReceivedAtMilliseconds },
            type: 'message-metadata',
          },
        ];
      }
      case EventType.RUN_FINISHED:
        return [
          // A run can end having emitted only empty blocks, which leaves the turn with nothing to
          // render, so the message says what the agent did rather than showing an empty reply.
          ...(hasRunProducedContent
            ? []
            : ([
                { id: options.assistantMessageId, type: 'text-start' },
                {
                  delta: 'The agent finished without a reply.',
                  id: options.assistantMessageId,
                  type: 'text-delta',
                },
                { id: options.assistantMessageId, type: 'text-end' },
              ] satisfies ChatMessageChunk[])),
          ...(isDefined(runStartedAtMilliseconds)
            ? ([
                {
                  metadata: {
                    runDurationMilliseconds: Math.max(
                      0,
                      isDefined(event.timestamp) && isDefined(runStartedAtEventMilliseconds)
                        ? event.timestamp - runStartedAtEventMilliseconds
                        : Date.now() - runStartedAtMilliseconds
                    ),
                  },
                  type: 'message-metadata',
                },
              ] satisfies ChatMessageChunk[])
            : []),
          { finishReason: 'stop', messageId: options.assistantMessageId, type: 'finish' },
        ];
      case EventType.RUN_ERROR:
        runErrorMessage = event.message;
        return [];
      case EventType.TEXT_MESSAGE_START:
        return deferPartStart({
          id: event.messageId,
          startChunk: { id: event.messageId, type: 'text-start' },
        });
      case EventType.TEXT_MESSAGE_CONTENT:
        return getPartDeltaChunks({
          delta: event.delta,
          deltaChunk: { delta: event.delta, id: event.messageId, type: 'text-delta' },
          id: event.messageId,
        });
      case EventType.TEXT_MESSAGE_END:
        return getPartEndChunks({
          id: event.messageId,
          endChunk: { id: event.messageId, type: 'text-end' },
        });
      case EventType.REASONING_MESSAGE_START:
        return deferPartStart({
          id: event.messageId,
          startChunk: { id: event.messageId, type: 'reasoning-start' },
        });
      case EventType.REASONING_MESSAGE_CONTENT:
        return getPartDeltaChunks({
          delta: event.delta,
          deltaChunk: { delta: event.delta, id: event.messageId, type: 'reasoning-delta' },
          id: event.messageId,
        });
      case EventType.REASONING_MESSAGE_END:
        return getPartEndChunks({
          id: event.messageId,
          endChunk: { id: event.messageId, type: 'reasoning-end' },
        });
      case EventType.TOOL_CALL_START:
        hasRunProducedContent = true;
        toolCallNamesById.set(event.toolCallId, event.toolCallName);
        toolCallArgumentsById.set(event.toolCallId, '');
        // The question is worth showing only once it is complete, so the ask-user call opens no
        // part and streams no arguments: its part arrives whole at the end of the call.
        return isAskUserCall(event.toolCallId)
          ? []
          : [
              {
                toolCallId: event.toolCallId,
                toolName: event.toolCallName,
                type: 'tool-input-start',
              },
            ];
      case EventType.TOOL_CALL_ARGS:
        toolCallArgumentsById.set(
          event.toolCallId,
          (toolCallArgumentsById.get(event.toolCallId) ?? '') + event.delta
        );
        return isAskUserCall(event.toolCallId)
          ? []
          : [
              {
                inputTextDelta: event.delta,
                toolCallId: event.toolCallId,
                type: 'tool-input-delta',
              },
            ];
      case EventType.TOOL_CALL_END: {
        const input = parseJsonOrFallbackToValue(toolCallArgumentsById.get(event.toolCallId) ?? '');
        if (isAskUserCall(event.toolCallId)) {
          const question = parseAgenticChatQuestion(input);
          return isDefined(question)
            ? [getAgenticChatQuestionPart({ id: event.toolCallId, question })]
            : [];
        }
        return [
          {
            input,
            toolCallId: event.toolCallId,
            toolName: toolCallNamesById.get(event.toolCallId) ?? 'unknown',
            type: 'tool-input-available',
          },
        ];
      }
      case EventType.TOOL_CALL_RESULT:
        // An answered question is the transcript's record of the exchange; the tool's own result
        // says nothing the card does not already show.
        return isAskUserCall(event.toolCallId)
          ? []
          : [
              {
                output: parseJsonOrFallbackToValue(event.content),
                toolCallId: event.toolCallId,
                type: 'tool-output-available',
              },
            ];
      case EventType.CUSTOM: {
        if (event.name !== TOOL_CALL_PARTIAL_RESULT_EVENT_NAME) {
          return [];
        }
        const partialResult = ToolCallPartialResultSchema.safeParse(event.value);
        return partialResult.success
          ? [
              {
                output: partialResult.data.partialResult,
                preliminary: true,
                toolCallId: partialResult.data.toolCallId,
                type: 'tool-output-available',
              },
            ]
          : [];
      }
      default:
        return [];
    }
  }

  return { getChunks, getRunErrorMessage: () => runErrorMessage };
}
